import React from 'react';
import { motion } from 'framer-motion';
import { Check, Zap, Crown, Building2 } from 'lucide-react';
import { Button } from './ui/button';

const PricingSection = () => {
  const plans = [
    {
      name: 'Family',
      icon: Zap,
      speed: '10 Mbps',
      price: '165.000',
      period: '/bulan',
      description: 'Cocok untuk rumah tangga dengan kebutuhan internet dasar.',
      features: [
        'Kecepatan hingga 10 Mbps',
        'Unlimited kuota',
        'Router WiFi gratis',
        'Instalasi gratis',
        'Dukungan teknis 24/7',
      ],
      popular: false,
      color: 'blue'
    },
    {
      name: 'Streaming',
      icon: Crown,
      speed: '20 Mbps',
      price: '220.000',
      period: '/bulan',
      description: 'Ideal untuk keluarga dengan banyak perangkat.',
      features: [
        'Kecepatan hingga 20 Mbps',
        'Unlimited kuota',
        'Router WiFi 6 gratis',
        'Instalasi prioritas',
        'Dukungan teknis 24/7',
        'Gaming mode',
      ],
      popular: true,
      color: 'red'
    },
    {
      name: 'Flash',
      icon: Building2,
      speed: '30 Mbps',
      price: '250.000',
      period: '/bulan',
      description: 'Solusi lengkap untuk kebutuhan bisnis Anda.',
      features: [
        'Kecepatan hingga 30 Mbps',
        'Unlimited kuota',
        'Router enterprise gratis',
        'Instalasi prioritas VIP',
        'Dedicated support',
        'SLA 99.9% uptime',
        'Backup koneksi',
      ],
      popular: false,
      color: 'blue'
    },
  ];

  const scrollToContact = () => {
    const element = document.querySelector('#kontak');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="paket" 
      className="py-24 md:py-32 bg-[#050505]"
      data-testid="pricing-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-medium tracking-widest uppercase text-brand-red mb-4 block">
            Paket Internet
          </span>
          <h2 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">
            Pilih Paket <span className="gradient-text-blue">Terbaik</span> untuk Anda
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Harga transparan tanpa biaya tersembunyi. Upgrade atau downgrade kapan saja Dan tentunya Tanpa FUP dan denda.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan, index) => {
            const Icon = plan.icon;
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`
                  relative bg-[#121212] rounded-2xl p-8 
                  ${plan.popular 
                    ? 'pricing-card-popular scale-105 md:scale-110 z-10' 
                    : 'border border-white/10 hover:border-brand-blue/30'
                  }
                  transition-all duration-300 hover:-translate-y-2
                `}
                data-testid={`pricing-card-${plan.name.toLowerCase()}`}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-brand-red text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wider">
                      Paling Populer
                    </span>
                  </div>
                )}

                {/* Plan Header */}
                <div className="text-center mb-8">
                  <div className={`
                    w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center
                    ${plan.popular 
                      ? 'bg-brand-red/20 border border-brand-red/30' 
                      : 'bg-brand-blue/10 border border-brand-blue/30'
                    }
                  `}>
                    <Icon className={`w-8 h-8 ${plan.popular ? 'text-brand-red' : 'text-brand-blue'}`} />
                  </div>
                  
                  <h3 className="font-outfit text-2xl font-bold text-white mb-2">
                    {plan.name}
                  </h3>
                  
                  <p className={`text-sm font-medium ${plan.popular ? 'text-brand-red' : 'text-brand-blue'}`}>
                    {plan.speed}
                  </p>
                </div>

                {/* Price */}
                <div className="text-center mb-6">
                  <div className="flex items-baseline justify-center">
                    <span className="text-2xl text-gray-400">Rp</span>
                    <span className="font-outfit text-5xl font-bold text-white mx-1">
                      {plan.price}
                    </span>
                  </div>
                  <span className="text-gray-500">{plan.period}</span>
                </div>

                {/* Description */}
                <p className="text-sm text-gray-400 text-center mb-8">
                  {plan.description}
                </p>

                {/* Features */}
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-3">
                      <div className={`
                        w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5
                        ${plan.popular ? 'bg-brand-red/20' : 'bg-brand-blue/20'}
                      `}>
                        <Check className={`w-3 h-3 ${plan.popular ? 'text-brand-red' : 'text-brand-blue'}`} />
                      </div>
                      <span className="text-sm text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <Button 
                  className={`
                    w-full py-4 rounded-xl font-semibold transition-all duration-300
                    ${plan.popular 
                      ? 'btn-primary text-white' 
                      : 'btn-secondary hover:bg-brand-blue/10'
                    }
                  `}
                  onClick={scrollToContact}
                  data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                >
                  Pilih Paket {plan.name}
                </Button>
              </motion.div>
            );
          })}
        </div>

        {/* Additional Info */}
        <motion.p 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="text-center text-gray-500 mt-12 text-sm"
        >
          * Harga belum termasuk PPN 11%. Syarat dan ketentuan berlaku.
        </motion.p>
      </div>
    </section>
  );
};

export default PricingSection;
